/**
 * Roles & Permissions admin page.
 *
 * Lets an admin (`can_manage_roles`) tick/untick any permission on any role,
 * tune the default disbursement limit + department, and reset a role back
 * to the hard-coded defaults defined in `services/permissions.py`.
 *
 * Changes are stored in the `role_definitions` Mongo collection (overrides).
 * The live matrix is read by every other part of the system, so a tweak
 * here propagates to:
 *   - the invite/edit dialogs in /admin/users
 *   - new user app_metadata at invite time
 *   - the Auth0 Post-Login Action (claims pulled from app_metadata)
 *
 * Existing users keep their currently-issued permissions until they're
 * edited — by design, so an admin can adjust a role definition without
 * silently downgrading anyone mid-session.
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { errorDetailMessage } from "@/lib/errorDetailMessage";
import apiClient from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Shield, RotateCcw, Save, AlertTriangle, Lock, Plus, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function RoleEditor() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("can_manage_roles");

  const [catalog, setCatalog] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newRole, setNewRole] = useState({ name: "", department: "Legal", description: "", copy_from: "none" });
  const [creating, setCreating] = useState(false);

  // Fetch the live role + permission matrix
  const load = async () => {
    try {
      const r = await apiClient.get("/admin/users/roles");
      setCatalog(r.data);
      if (!selectedRole && r.data.roles?.length) {
        setSelectedRole(r.data.roles[0].name);
      }
    } catch (e) {
      toast.error("Failed to load roles: " + errorDetailMessage(e, e.message));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Whenever the selected role changes, snapshot its current state into the draft
  useEffect(() => {
    if (!catalog || !selectedRole) return;
    const r = catalog.roles.find(x => x.name === selectedRole);
    if (!r) return;
    setDraft({
      permissions: new Set(r.permissions),
      department: r.department,
      description: r.description,
      default_disbursement_limit: r.default_disbursement_limit,
      source: r.source,
    });
  }, [selectedRole, catalog]);

  const togglePermission = (perm) => {
    if (!draft) return;
    const next = new Set(draft.permissions);
    if (next.has(perm)) next.delete(perm); else next.add(perm);
    setDraft({ ...draft, permissions: next });
  };

  const saveRole = async () => {
    if (!draft || !selectedRole) return;
    setSaving(true);
    try {
      await apiClient.patch(`/admin/users/roles/${encodeURIComponent(selectedRole)}`, {
        permissions: Array.from(draft.permissions),
        department: draft.department,
        description: draft.description,
        default_disbursement_limit: Number(draft.default_disbursement_limit) || 0,
      });
      toast.success(`Role "${selectedRole}" updated`);
      await load();
    } catch (e) {
      toast.error("Save failed: " + errorDetailMessage(e, e.message));
    } finally { setSaving(false); }
  };

  const resetRole = async () => {
    if (!selectedRole) return;
    if (!window.confirm(`Reset "${selectedRole}" to default permissions? This drops any admin overrides.`)) return;
    setResetting(true);
    try {
      await apiClient.post(`/admin/users/roles/${encodeURIComponent(selectedRole)}/reset`);
      toast.success(`Role "${selectedRole}" reverted to defaults`);
      await load();
    } catch (e) {
      toast.error("Reset failed: " + errorDetailMessage(e, e.message));
    } finally { setResetting(false); }
  };

  const createRole = async () => {
    const name = newRole.name.trim();
    if (name.length < 2) { toast.error("Role name must be at least 2 characters"); return; }
    setCreating(true);
    try {
      await apiClient.post("/admin/users/roles", {
        name,
        department: newRole.department.trim() || "Legal",
        description: newRole.description.trim(),
        copy_from: newRole.copy_from === "none" ? null : newRole.copy_from,
      });
      toast.success(`Role "${name}" created — now tick its rights and save`);
      setShowCreate(false);
      setNewRole({ name: "", department: "Legal", description: "", copy_from: "none" });
      await load();
      setSelectedRole(name);
    } catch (e) {
      toast.error("Create failed: " + errorDetailMessage(e, e.message));
    } finally { setCreating(false); }
  };

  const deleteRole = async () => {
    if (!selectedRole) return;
    if (!window.confirm(`Delete the custom role "${selectedRole}"? Users must be reassigned first.`)) return;
    setResetting(true);
    try {
      await apiClient.delete(`/admin/users/roles/${encodeURIComponent(selectedRole)}`);
      toast.success(`Role "${selectedRole}" deleted`);
      setSelectedRole(null);
      await load();
    } catch (e) {
      toast.error("Delete failed: " + errorDetailMessage(e, e.message));
    } finally { setResetting(false); }
  };

  const allPermissions = useMemo(
    () => Object.entries(catalog?.permissions || {}),
    [catalog]
  );

  // Section-by-section grouping (from the backend glossary). Falls back to a
  // single "All permissions" group if the API predates sections.
  const sections = useMemo(() => {
    const glossary = catalog?.permissions || {};
    const secs = catalog?.sections;
    if (Array.isArray(secs) && secs.length) {
      return secs.map(s => ({
        section: s.section,
        items: (s.items || []).filter(k => glossary[k] !== undefined),
      }));
    }
    return [{ section: "All permissions", items: Object.keys(glossary) }];
  }, [catalog]);

  const toggleSection = (items, allOn) => {
    if (!draft) return;
    const next = new Set(draft.permissions);
    items.forEach(p => { if (allOn) next.delete(p); else next.add(p); });
    setDraft({ ...draft, permissions: next });
  };

  if (!canManage) {
    return (
      <div className="p-8 max-w-2xl">
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <Lock className="w-5 h-5 text-amber-700 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-900">Permission required</p>
            <p className="text-sm text-amber-800 mt-1">
              You need <code className="font-mono text-xs">can_manage_roles</code> to edit role definitions.
              Ask an Admin to grant it on your profile or change role.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!catalog || !draft) {
    return <div className="p-8 text-muted-foreground">Loading role catalog…</div>;
  }

  const currentRole = catalog.roles.find(r => r.name === selectedRole);
  const isOverridden = currentRole?.source === "overridden" || currentRole?.source === "custom";
  const isCustom = currentRole?.source === "custom";

  return (
    <div className="space-y-6 max-w-7xl" data-testid="role-editor">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            Roles &amp; Permissions
          </h1>
          <p className="text-muted-foreground mt-1">
            Tune the role → permission matrix without redeploying. Defaults live in code; your tweaks live in MongoDB and take effect on the next user invite or edit.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="new-role-btn">
          <Plus className="w-4 h-4 mr-2" /> New role
        </Button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Role picker */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Roles ({catalog.roles.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y" data-testid="role-list">
              {catalog.roles.map(r => (
                <li key={r.name}>
                  <button
                    onClick={() => setSelectedRole(r.name)}
                    className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${
                      selectedRole === r.name ? "bg-primary/5 border-l-4 border-primary" : ""
                    }`}
                    data-testid={`role-row-${r.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{r.name}</span>
                      {r.source !== "default" && (
                        <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 text-xs">
                          custom
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                      <span>{r.department}</span>
                      <span>·</span>
                      <span>{r.permissions.length} perms</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Editor */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {selectedRole}
                  {isOverridden && (
                    <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 text-xs">
                      admin override active
                    </Badge>
                  )}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {currentRole?.permissions.length} of {allPermissions.length} permissions enabled
                </p>
              </div>
              <div className="flex gap-2">
                {isCustom && (
                  <Button
                    variant="outline"
                    className="text-rose-600 border-rose-300 hover:bg-rose-50"
                    onClick={deleteRole}
                    disabled={resetting}
                    data-testid="delete-role-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete role
                  </Button>
                )}
                {isOverridden && !isCustom && (
                  <Button
                    variant="outline"
                    onClick={resetRole}
                    disabled={resetting || selectedRole === "Admin"}
                    data-testid="reset-role-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {resetting ? "Resetting…" : "Reset"}
                  </Button>
                )}
                <Button
                  onClick={saveRole}
                  disabled={saving || selectedRole === "Admin"}
                  data-testid="save-role-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {selectedRole === "Admin" && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
                <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-amber-900">Admin role is locked</p>
                  <p className="text-amber-800">Admins always have every permission. This is enforced server-side so an admin can never accidentally lock themselves out.</p>
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="role-dept">Department</Label>
                <Input
                  id="role-dept"
                  value={draft.department || ""}
                  onChange={e => setDraft({ ...draft, department: e.target.value })}
                  disabled={selectedRole === "Admin"}
                  data-testid="role-dept-input"
                />
              </div>
              <div>
                <Label htmlFor="role-limit">Default disbursement limit (£)</Label>
                <Input
                  id="role-limit"
                  type="number"
                  min={0}
                  value={draft.default_disbursement_limit ?? 0}
                  onChange={e => setDraft({ ...draft, default_disbursement_limit: e.target.value })}
                  disabled={selectedRole === "Admin"}
                  data-testid="role-limit-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="role-desc">Description</Label>
                <Textarea
                  id="role-desc"
                  rows={2}
                  value={draft.description || ""}
                  onChange={e => setDraft({ ...draft, description: e.target.value })}
                  disabled={selectedRole === "Admin"}
                  data-testid="role-desc-input"
                />
              </div>
            </div>

            <Separator />

            {/* Permissions — section by section */}
            <div>
              <h3 className="font-semibold mb-3">Permissions ({draft.permissions.size}/{allPermissions.length})</h3>
              <div className="space-y-5" data-testid="perm-grid">
                {sections.map(({ section, items }) => {
                  const glossary = catalog.permissions || {};
                  const checkedCount = items.filter(p => draft.permissions.has(p)).length;
                  const allOn = checkedCount === items.length && items.length > 0;
                  return (
                    <div key={section} data-testid={`perm-section-${section.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                          {section}
                          <span className="text-[11px] font-normal text-muted-foreground">{checkedCount}/{items.length}</span>
                        </div>
                        {selectedRole !== "Admin" && (
                          <button type="button"
                            onClick={() => toggleSection(items, allOn)}
                            className="text-[11px] text-primary hover:underline"
                            data-testid={`perm-section-toggle-${section.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                            {allOn ? "Untick all" : "Tick all"}
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {items.map(perm => {
                          const checked = draft.permissions.has(perm);
                          return (
                            <label
                              key={perm}
                              className={`flex items-start gap-3 rounded-md border p-2.5 cursor-pointer transition-colors ${
                                checked ? "border-primary/40 bg-primary/5" : "border-slate-200 hover:bg-slate-50"
                              } ${selectedRole === "Admin" ? "opacity-60 cursor-not-allowed" : ""}`}
                              data-testid={`perm-${perm}`}
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() => togglePermission(perm)}
                                disabled={selectedRole === "Admin"}
                                className="mt-0.5"
                              />
                              <div className="text-sm flex-1">
                                <span className="font-medium text-slate-800 text-[13px]">{glossary[perm]}</span>
                                <p className="text-muted-foreground mt-0.5"><code className="font-mono text-[10px]">{perm}</code></p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* New role dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md grid-cols-1" aria-describedby={undefined} data-testid="new-role-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" /> Create a new role
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="nr-name">Role name</Label>
              <Input
                id="nr-name" placeholder="e.g. Paralegal, Legal Assistant"
                value={newRole.name}
                onChange={e => setNewRole(s => ({ ...s, name: e.target.value }))}
                data-testid="new-role-name"
              />
            </div>
            <div>
              <Label htmlFor="nr-dept">Department</Label>
              <Input
                id="nr-dept" placeholder="Legal"
                value={newRole.department}
                onChange={e => setNewRole(s => ({ ...s, department: e.target.value }))}
                data-testid="new-role-dept"
              />
            </div>
            <div>
              <Label>Start from (copies that role's ticks)</Label>
              <Select value={newRole.copy_from} onValueChange={v => setNewRole(s => ({ ...s, copy_from: v }))}>
                <SelectTrigger data-testid="new-role-copyfrom"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Blank — no permissions</SelectItem>
                  {catalog.roles.map(r => (
                    <SelectItem key={r.name} value={r.name}>{r.name} ({r.permissions.length} perms)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="nr-desc">Description</Label>
              <Textarea
                id="nr-desc" rows={2} placeholder="What this role is for"
                value={newRole.description}
                onChange={e => setNewRole(s => ({ ...s, description: e.target.value }))}
                data-testid="new-role-desc"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createRole} disabled={creating} data-testid="new-role-create-btn">
              {creating ? "Creating…" : "Create role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
